import { format, parseISO } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { AlertCircleIcon } from "@icons";
import type { CloseTemplateOverlappingClose } from "@common/schemas/close-template.schema";
import styles from "./CloseTemplateOverlapWarning.module.scss";

interface Props {
    overlappingCloses: CloseTemplateOverlappingClose[];
}

/**
 * Non-blocking warning shown in the apply-template preview when the previewed
 * period intersects existing closes from the same template + entity. Apply
 * still works; this is purely informational so the operator can avoid creating
 * unintended duplicates.
 */
export const CloseTemplateOverlapWarning = ({ overlappingCloses }: Props) => (
    <div className={styles.banner} role="status">
        <span className={styles.icon} aria-hidden>
            <AlertCircleIcon />
        </span>
        <div>
            <div className={styles.title}>
                {overlappingCloses.length === 1
                    ? "This template was already applied for an overlapping period"
                    : `This template was already applied ${overlappingCloses.length} times for overlapping periods`}
            </div>
            <ul className={styles.periodsList}>
                {overlappingCloses.map((overlap) => (
                    <li key={overlap.closeId}>
                        {formatPeriod(overlap.startDate, overlap.endDate)}
                    </li>
                ))}
            </ul>
            <div className={styles.body}>
                Applying again will create another close that overlaps the
                existing one(s).
            </div>
        </div>
    </div>
);

function formatPeriod(
    startDate: string | null,
    endDate: string | null,
): string {
    if (startDate && endDate) {
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    if (startDate) {
        return `from ${formatDate(startDate)}`;
    }
    if (endDate) {
        return `until ${formatDate(endDate)}`;
    }
    return "an unspecified period";
}

function formatDate(date: string): string {
    return format(parseISO(date), fullDateFullYearFormat);
}
